/**
 * How the Sports screens read (CAE-1594) — the cache keys the three pages share
 * and the retry policy they all want.
 *
 * Kept out of the pages because the keys only work if everyone agrees on them:
 * a save invalidates the list from the edit page, and the create page
 * invalidates the list from somewhere else again.
 */
import { AdminApiError } from '../../api/client';

/**
 * The Sports list.
 *
 * Shared with the contest form, which reads the same endpoint to offer a
 * Contest's Sport (`pages/contests/contest-form-page.tsx`) — so invalidating
 * this after a save refreshes both screens rather than only this one.
 */
export const SPORTS_QUERY_KEY = ['admin', 'sports'] as const;

/**
 * One Sport. Singular, and deliberately *not* under the list's key.
 *
 * TanStack matches query keys by prefix, so a detail key of
 * `['admin', 'sports', id]` would be invalidated by every `invalidateQueries`
 * on the list — which a save does, to refresh the row's counts. The Sport would
 * then be re-read twice for one save: once because it changed, once as
 * collateral. Keeping the two trees apart lets each invalidation mean only what
 * it says.
 */
export const sportQueryKey = (sportId: string) => ['admin', 'sport', sportId] as const;

/**
 * A Sport's audit history — *under* the Sport's own key on purpose: a change to
 * the Sport is exactly a change to its history, so one invalidation refreshes
 * both.
 */
export const sportAuditQueryKey = (sportId: string) =>
  ['admin', 'sport', sportId, 'audit'] as const;

/**
 * A Sport's club roster (CAE-1595, API-131) — *outside* the Sport's own key,
 * unlike the audit history.
 *
 * A club's images are not the Sport row, and neither write touches the other:
 * saving the Sport does not change a logo, and attaching one does not bump the
 * Sport's version. Under `sportQueryKey` every Save would re-read 30 clubs for
 * nothing, and every attach would re-read the Sport under an operator's
 * unsaved draft — which is the one read on that page that must not happen by
 * accident (see `sport-edit-page.tsx` on `readSportNow`).
 */
export const clubRosterQueryKey = (sportId: string) => ['admin', 'sport-clubs', sportId] as const;

/**
 * How many audit rows a page asks for.
 *
 * API-130 serves 50 by default and caps a single request at 200, and INV-15
 * writes one row per *field* changed — so a save touching five fields is five
 * rows and a Sport passes 50 in roughly ten sittings. A panel that asked once
 * and said nothing would then quietly stop at the newest 50 under a heading
 * claiming to list every change; the page asks for them a page at a time and
 * says how many it is holding instead.
 */
export const AUDIT_PAGE_SIZE = 50;

/** Two retries after the first attempt, matching TanStack's own default shape. */
const MAX_RETRIES = 2;

/**
 * Retry the network, never the request.
 *
 * The app's `QueryClient` is constructed bare, so every query retries three
 * times with backoff. On a 404 that is the wrong answer twice over: it asks
 * three times for a row the API has already said is absent, and it leaves the
 * screen saying "Loading sport…" for about seven seconds before it can show the
 * not-found state. Nothing about a 4xx improves by being asked again — the
 * request is what is wrong — so only a server or transport failure is retried.
 */
export const retrySportQuery = (failureCount: number, error: unknown): boolean => {
  if (error instanceof AdminApiError && error.status >= 400 && error.status < 500) {
    return false;
  }
  return failureCount < MAX_RETRIES;
};
